// CSV の列定義を、自動生成された zod のテーブル定義から導き出すモジュール。
// 列名を手で並べた表を持たずに済ませ、スキーマが増減したら CSV も自動で追従するようにする。
//
// 型の見分け方について:
//   zod のクラスへの instanceof も、_def のような内部フィールドの参照も使わない。
//   どちらもライブラリの内部構造に依存し、zod のバージョンが上がると静かに壊れるため。
//   代わりに「代表値を試しに通してみて受理されるか」という公開された振る舞いだけで判定する。
//   真偽値・数値・文字列の3つは互いの代表値を受理しないため、これで一意に決まる。

import type { SliceKey } from "../../../../api/dbSlice";
import { collectionKeyToEntityName } from "../../../../api/IndexedDB/generated/collectionMap";
import { idbEntityDefs } from "../../../../api/IndexedDB/generated/entityDefs";
import type { MasterImportCellValue } from "../../../../schema/console/master_import";
import { readProp } from "../../../../util/typed";

export type MasterCsvColumnKind = "string" | "number" | "boolean";

export type MasterCsvColumn = {
  name: string;
  kind: MasterCsvColumnKind;
  // 空セルを「未入力」として受け付けられる列かどうか
  nullable: boolean;
};

// CSV の1列目に固定で置く列。
const ID_COLUMN_NAME = "id";

// システムが自動で管理するため CSV には出さない列。
const SYSTEM_MANAGED_COLUMN_NAMES = ["createdAt", "updatedAt", "deleted_at"];

// 値を試しに通せるもの。zod のスキーマのうち本モジュールが使う操作だけを表す。
type ValueChecker = { safeParse(value: unknown): { success: boolean } };

// 列ごとの定義を持つもの。zod のオブジェクト定義が公開している shape に相当する。
type ColumnShapeHolder = { shape: Record<string, unknown> };

function isValueChecker(value: unknown): value is ValueChecker {
  const isObject = typeof value === "object" && value !== null;
  if (isObject) {
    return typeof readProp(value, "safeParse") === "function";
  }
  return false;
}

function hasColumnShape(value: unknown): value is ColumnShapeHolder {
  const isObject = typeof value === "object" && value !== null;
  if (isObject) {
    const shape = readProp(value, "shape");
    return typeof shape === "object" && shape !== null;
  }
  return false;
}

// テーブル定義から「列名 → その列の検証定義」を取り出す。並び順は定義に書かれた順。
function columnDefinitionsOf(collectionKey: SliceKey): Record<string, unknown> {
  const entityName = collectionKeyToEntityName[collectionKey];
  const tableSchema = idbEntityDefs[entityName].schema;
  if (hasColumnShape(tableSchema)) {
    return tableSchema.shape;
  }
  throw new Error(`${collectionKey} のテーブル定義から列を取り出せません(自動生成物の形が変わっています)。`);
}

// 代表値を試しに通して列の型を決める。どれも受理されない列は CSV で扱えない。
function kindOfColumn(checker: ValueChecker, columnName: string): MasterCsvColumnKind {
  if (checker.safeParse(true).success) {
    return "boolean";
  }
  if (checker.safeParse(0).success) {
    return "number";
  }
  if (checker.safeParse("").success) {
    return "string";
  }
  throw new Error(`列 ${columnName} は CSV で扱える型(文字列・数値・真偽値)ではありません。`);
}

function columnOf(columnName: string, columnDefinition: unknown): MasterCsvColumn {
  if (isValueChecker(columnDefinition)) {
    return {
      name: columnName,
      kind: kindOfColumn(columnDefinition, columnName),
      nullable: columnDefinition.safeParse(null).success,
    };
  }
  throw new Error(`列 ${columnName} の定義を読み取れません(自動生成物の形が変わっています)。`);
}

// 指定テーブルの CSV 列を、出力する順番で返す。1列目は必ず id。
export function csvColumnsOf(collectionKey: SliceKey): MasterCsvColumn[] {
  const columnDefinitions = columnDefinitionsOf(collectionKey);
  const businessColumnNames = Object.keys(columnDefinitions).filter((columnName) => {
    const isIdColumn = columnName === ID_COLUMN_NAME;
    const isSystemManaged = SYSTEM_MANAGED_COLUMN_NAMES.includes(columnName);
    return !isIdColumn && !isSystemManaged;
  });
  const orderedNames = [ID_COLUMN_NAME, ...businessColumnNames];
  return orderedNames.map((columnName) => columnOf(columnName, columnDefinitions[columnName]));
}

// 数値を10進表記にする。桁が大きい値や小さい値が指数表記になるのを避ける。
function decimalTextOf(value: number): string {
  const plainText = String(value);
  const isExponential = plainText.includes("e") || plainText.includes("E");
  if (isExponential) {
    return value.toLocaleString("en-US", { useGrouping: false, maximumFractionDigits: 20 });
  }
  return plainText;
}

// 表計算ソフトに「この値は計算式ではなく文字」と伝えるための印。
// Excel などは先頭のこの記号を文字列指定として読み取り、画面には表示しない。
// 書き出しで付け、取り込みで外すため、利用者から見た値は往復しても変わらない。
const TEXT_MARK = "'";

// セルの先頭にあると、表計算ソフトが計算式や命令として解釈してしまう文字。
// = + @ は計算式や旧来の命令の始まりとして扱われ、- は符号付きの計算式として扱われる。
// タブと復帰は値をセルの外へ押し出し、隣のセルを計算式として書き換える手口に使われる。
// 印そのものを含めるのは、元から印で始まっていた値が取り込みで別の値へ変わらないようにするため。
const FORMULA_LEADING_CHARACTERS = ["=", "+", "-", "@", "\t", "\r", TEXT_MARK];

// 表計算ソフトで開いたときに計算式として実行されうる書き出し値かどうか。
function isFormulaLike(text: string): boolean {
  const leadingCharacter = text.slice(0, 1);
  return FORMULA_LEADING_CHARACTERS.includes(leadingCharacter);
}

// 文字列の値を書き出す形にする。計算式として実行されうる値には文字列指定の印を先頭に足す。
function markedTextOf(text: string): string {
  if (isFormulaLike(text)) {
    return TEXT_MARK + text;
  }
  return text;
}

// 書き出しで付けた文字列指定の印を外す。印は必ず1つだけ付くため、
// 2つ以上並んでいても外すのは先頭の1つだけにして、利用者が入力した印を残す。
function unmarkedTextOf(cell: string): string {
  if (cell.startsWith(TEXT_MARK)) {
    return cell.slice(TEXT_MARK.length);
  }
  return cell;
}

// 行の値を CSV のセル文字列にする。未入力は空セルで表す。
//
// 文字列の列だけは、表計算ソフトが計算式として実行してしまう値に文字列指定の印を付けて書き出す。
// 数値の列と真偽値の列を対象から外すのは、-1 のような値に印が付くと取り込み時に数値として読めなくなるため。
export function cellOfValue(value: unknown, column: MasterCsvColumn): string {
  const isMissing = value === null || value === undefined;
  if (isMissing) {
    return "";
  }
  if (column.kind === "number" && typeof value === "number") {
    return decimalTextOf(value);
  }
  if (column.kind === "string") {
    return markedTextOf(String(value));
  }
  return String(value);
}

export type CellParseResult = { ok: true; value: MasterImportCellValue } | { ok: false; reason: string };

// 数値として受け付ける書き方。先頭の符号1つ、半角数字、小数点1つだけを許す。
// エクスポートは10進表記でしか出さないため、16進や2進の接頭辞・指数表記・前後の空白を
// 受け入れる理由がない。読み手の想定と違う値が黙って入るのを防ぐ。
const DECIMAL_NUMBER_PATTERN = /^[+-]?\d+(\.\d+)?$/;

// 数値列のセルを読む。空白だけの値は未入力とも数値とも解釈しない。
function numberValueOfCell(cell: string): CellParseResult {
  const isBlank = cell.trim() === "";
  if (isBlank) {
    return { ok: false, reason: "空白だけの値は使えません。数値を入力してください" };
  }
  const hasSurroundingSpace = cell !== cell.trim();
  if (hasSurroundingSpace) {
    return { ok: false, reason: "値の前後に空白があります。空白を取り除いて半角数字だけにしてください" };
  }
  const parsed = Number(cell);
  const isPlainDecimalText = DECIMAL_NUMBER_PATTERN.test(cell);
  if (isPlainDecimalText && Number.isFinite(parsed)) {
    return { ok: true, value: parsed };
  }
  // 計算上は数に直せるのに書き方だけが認められない値は、何が使えないのかを名指しで伝える。
  const isUnsupportedNotation = !isPlainDecimalText && Number.isFinite(parsed);
  if (isUnsupportedNotation) {
    return { ok: false, reason: "10進の半角数字で入力してください。0x や 0b で始まる書き方、1e2 のような指数表記は使えません" };
  }
  return { ok: false, reason: "数値ではありません。半角数字で入力してください" };
}

// 真偽値列のセルを読む。表記ゆれを受け入れると意味が変わるため綴りは2種類だけ認める。
function booleanValueOfCell(cell: string): CellParseResult {
  if (cell === "true") {
    return { ok: true, value: true };
  }
  if (cell === "false") {
    return { ok: true, value: false };
  }
  return { ok: false, reason: "true または false を入れてください" };
}

// CSV のセル文字列を行の値に戻す。読めない値は、その場で直せる理由を付けて返す。
//
// null を許さない文字列列の空セルは、あえてエラーにせず空文字として受け取る。
// 業務上は空でよい列も、テーブル定義の上では null を許さない文字列列として表されているものがあり
// (発注者の部署名や場所の記入欄など)、一律に拒否すると正常なデータが取り込めなくなるため。
// 本当に入力が欠かせない列かどうかはサーバー側の検証が判断する。
//
// 文字列の列は、書き出しで付けた文字列指定の印を外してから値にする。
// 数値の列と真偽値の列は書き出しでも印を付けないため、そのまま読む。
export function valueOfCell(cell: string, column: MasterCsvColumn): CellParseResult {
  const isEmptyCell = cell === "";
  if (column.kind === "string") {
    const meansNotEntered = isEmptyCell && column.nullable;
    if (meansNotEntered) {
      return { ok: true, value: null };
    }
    return { ok: true, value: unmarkedTextOf(cell) };
  }
  if (isEmptyCell) {
    if (column.nullable) {
      return { ok: true, value: null };
    }
    if (column.kind === "number") {
      return { ok: false, reason: "空欄にできません。数値を入力してください" };
    }
    return { ok: false, reason: "空欄にできません。true または false を入れてください" };
  }
  if (column.kind === "number") {
    return numberValueOfCell(cell);
  }
  return booleanValueOfCell(cell);
}
